/**
 * production.api.ts — Plan de producción, forecast y anomalías (F2).
 *
 * Espejo de `aws-serverless/src/models/production-plan.model.ts`. El cliente
 * inyecta el tenant en la URL (`client.ts`), por eso las rutas van sin él.
 */

import { apiClient } from './client';

export const PRODUCTION_PLAN_STATUSES = ['PLANNED', 'IN_PROGRESS', 'DONE'] as const;
export type ProductionPlanStatus = (typeof PRODUCTION_PLAN_STATUSES)[number];

export const CURVE_SOURCES = ['PROTOCOL', 'CROP_DEFAULT', 'EXPLICIT'] as const;
export type CurveSource = (typeof CURVE_SOURCES)[number];

export type FenologiaStage =
  | 'PREPARACION_SUELO'
  | 'SIEMBRA'
  | 'GERMINACION'
  | 'EMERGENCIA'
  | 'ESTABLECIMIENTO'
  | 'VEGETATIVO'
  | 'FLORACION'
  | 'FRUCTIFICACION'
  | 'MADURACION'
  | 'COSECHA'
  | 'POST_COSECHA';

export interface ProductionPlan {
  moduleId: string;
  planKey: string;
  planId: string;
  tenantId: string;
  projectId: string;
  loteId: string;
  seq: number;
  stageCode: FenologiaStage;
  plannedStart: string;
  plannedEnd: string;
  plannedDurationDays: number;
  /** % acumulado del rendimiento al terminar esta etapa (monótono, última = 100). */
  yieldCurvePct: number;
  actualStart?: string;
  actualEnd?: string;
  /** real − plan, en días. Positivo = atrasado, negativo = adelantado. */
  deviationDays?: number;
  cropName?: string;
  protocolId?: string;
  curveSource: CurveSource;
  status: ProductionPlanStatus;
  createdAt: string;
  updatedAt?: string;
}

export const FORECAST_HORIZONS = [7, 15, 60] as const;
export type ForecastHorizon = (typeof FORECAST_HORIZONS)[number];

export interface ForecastScenarios {
  conservador: number;
  base: number;
  ideal: number;
}

export interface ForecastInputsSnapshot {
  baseYieldKg: number;
  curveSource: CurveSource;
  plantingDate: string;
  asOfDate: string;
  daysFromPlanting: number;
  deviationDays: number;
  cumulativePctNow: number;
  cumulativePctAtHorizon: number;
  packoutPct: number | null;
  pestPressure: 'BAJO' | 'MEDIO' | 'ALTO' | 'CRITICO' | null;
  packoutFactor: number;
  pestFactor: number;
  healthMultiplier: number;
  spreadPct: number;
}

export interface Forecast {
  targetId: string;
  forecastKey: string;
  forecastId: string;
  tenantId: string;
  targetType: 'MODULE' | 'PROJECT';
  type: 'YIELD';
  horizonDays: ForecastHorizon;
  generatedAt: string;
  /** Kg esperados DENTRO de la ventana del horizonte (incremental, no acumulado). */
  scenarios: ForecastScenarios;
  unit: string;
  confidencePct: number;
  inputs: ForecastInputsSnapshot;
}

export const ANOMALY_TYPES = [
  'DESVIACION_FENOLOGICA',
  'PLAGA',
  'RIEGO',
  'MAQUINARIA',
  'PERSONAL',
  'CLIMA',
  'CALIDAD',
  'SENSOR',
  'OTRO',
] as const;
export type AnomalyType = (typeof ANOMALY_TYPES)[number];

export const ANOMALY_STATUSES = ['ABIERTA', 'EN_PROCESO', 'RESUELTA', 'DESCARTADA'] as const;
export type AnomalyStatus = (typeof ANOMALY_STATUSES)[number];

export const ANOMALY_SEVERITIES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'] as const;
export type AnomalySeverity = (typeof ANOMALY_SEVERITIES)[number];

export interface Anomaly {
  projectId: string;
  anomalyKey: string;
  anomalyId: string;
  tenantId: string;
  tipo: AnomalyType;
  severity: AnomalySeverity;
  title: string;
  description?: string;
  moduleId?: string;
  loteId?: string;
  responsibleUserId?: string;
  status: AnomalyStatus;
  detectedAt: string;
  source: 'RULE' | 'TELEGRAM' | 'DASHBOARD';
  resolucion?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
  updatedAt?: string;
}

// ── Plan de producción ──────────────────────────────────────────────────────

export const fetchProductionPlan = async (
  projectId: string,
  moduleId?: string,
  ranchoId?: string | null,
): Promise<ProductionPlan[]> =>
  (await apiClient.get('/api/production/plans', { params: { projectId, moduleId, ranchoId: ranchoId || undefined } })).data.items ?? [];

// ── Forecast (solo lectura) ──────────────────────────────────────────────────

export const fetchForecasts = async (
  targetId: string,
  params: { horizonDays?: ForecastHorizon; limit?: number } = {},
): Promise<Forecast[]> =>
  (await apiClient.get(`/api/production/forecasts/${targetId}`, { params })).data.items ?? [];

// ── Anomalías ─────────────────────────────────────────────────────────────

export const fetchAnomalies = async (
  params: { projectId?: string; status?: AnomalyStatus; limit?: number; ranchoId?: string | null } = {},
): Promise<Anomaly[]> =>
  (await apiClient.get('/api/production/anomalies', { params: { ...params, ranchoId: params.ranchoId || undefined } })).data.items ?? [];
